"use client";

import { useTranslations } from "next-intl";

import type { ServiceEvaluationResult } from "@louez/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@louez/ui";

type QuoteEstimateResultProps = {
  currency: string;
  result: ServiceEvaluationResult;
};

export const QuoteEstimateResult = ({
  currency,
  result,
}: QuoteEstimateResultProps) => {
  const t = useTranslations("storefront.services");
  const money = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  });

  return (
    <div className="bg-muted/40 space-y-3 rounded-xl border p-4">
      <p className="text-sm font-semibold">{t("estimateSummary")}</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("line")}</TableHead>
              <TableHead className="whitespace-nowrap">{t("quantity")}</TableHead>
              <TableHead className="text-right">{t("lineTotal")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.lines.map((line) => (
              <TableRow key={line.code}>
                <TableCell className="max-w-[16rem] truncate sm:max-w-none">
                  {line.description}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {line.quantity} {line.unit}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {money.format(line.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <dl className="ml-auto grid max-w-xs grid-cols-2 gap-1.5 text-sm">
        <dt className="text-muted-foreground">{t("subtotal")}</dt>
        <dd className="text-right">{money.format(result.subtotal)}</dd>
        <dt className="text-muted-foreground">{t("tax")}</dt>
        <dd className="text-right">{money.format(result.taxAmount)}</dd>
        <dt className="font-semibold">{t("total")}</dt>
        <dd className="text-right font-semibold">
          {money.format(result.total)}
        </dd>
      </dl>
    </div>
  );
};
